import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  inject,
  INJECTOR,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthService } from '@fapp/authentication';
import { CoreModule } from '@fapp/core';
import {
  StrawberryDateTimeTransformer,
  StrawberryDateTransformer,
} from '@fapp/directives';
import { LayoutsModule } from '@fapp/layouts';
import { PageNotFoundComponent } from '@fapp/pages';
import { PipesModule } from '@fapp/pipes';
import {
  TUI_ALERT_POSITION,
  TuiAlert,
  TuiDialog,
  TuiFlagPipe,
  tuiIconResolverProvider,
  tuiNumberFormatProvider,
  TuiRoot,
} from '@taiga-ui/core';
import {
  TUI_EDITOR_DEFAULT_EXTENSIONS,
  TUI_EDITOR_EXTENSIONS,
} from '@taiga-ui/editor';
import { NG_EVENT_PLUGINS } from '@taiga-ui/event-plugins';
import {
  TUI_DATE_TIME_VALUE_TRANSFORMER,
  TUI_DATE_VALUE_TRANSFORMER,
  TUI_VALIDATION_ERRORS,
  TuiProgress,
} from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

function appInitFactory(auth: AuthService): () => Observable<any> {
  //return () => auth.init().pipe(delay(1000000));
  return () =>
    auth
      .optimisticWriteCurrentUser()
      .pipe(tap(() => auth.init().pipe(take(1)).subscribe()));
}

// noinspection JSUnusedGlobalSymbols
@NgModule({
  declarations: [AppComponent, PageNotFoundComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CoreModule,
    LayoutsModule,
    PipesModule,
    TuiRoot,
    TuiAlert,
    TuiDialog,
    ...TuiProgress,
    TuiFlagPipe,
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = appInitFactory(inject(AuthService));
      return initializerFn();
    }),
    {
      provide: TUI_EDITOR_EXTENSIONS,
      deps: [INJECTOR],
      useFactory: () => [...TUI_EDITOR_DEFAULT_EXTENSIONS],
    },
    { provide: TUI_ALERT_POSITION, useValue: 'auto 2rem 3rem auto' },
    tuiIconResolverProvider(icon => {
      if (String(icon).startsWith('mat::')) {
        return `assets/icons/material/${String(icon).replace('mat::', '')}.svg`;
      }
      return !icon || icon.includes('/')
        ? icon
        : `assets/taiga-ui/icons/${icon.replace('@tui.', '').split('.').join('/')}.svg`;
    }),
    {
      provide: POLYMORPHEUS_CONTEXT,
      useValue: undefined,
    },
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: {
        required: $localize`This field is required`,
        email: $localize`Enter a valid email`,
        maxlength: ({ requiredLength }: { requiredLength: string }) =>
          $localize`Maximum length — ${requiredLength}`,
        minlength: ({ requiredLength }: { requiredLength: string }) =>
          $localize`Minimum length — ${requiredLength}`,
        pwdStrength: ({ errors }: { errors: string[] }) => errors.join('\n'),
        matching: $localize`Passwords do not match`,
      },
    },
    // TODO: Make this dynamic based on the user's locale
    tuiNumberFormatProvider({
      decimalSeparator: '.',
      thousandSeparator: ',',
    }),
    {
      provide: TUI_DATE_VALUE_TRANSFORMER,
      useClass: StrawberryDateTransformer,
    },
    {
      provide: TUI_DATE_TIME_VALUE_TRANSFORMER,
      useClass: StrawberryDateTimeTransformer,
    },
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    NG_EVENT_PLUGINS,
  ],
})
export class AppModule {}
